import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { BalanceSheetReport } from 'src/app/shared/models/finance';
import { CommonService } from 'src/app/shared/services/common.service';
import { FinanceService } from 'src/app/shared/services/finance.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';



@Component({
  selector: 'app-balance-sheet',
  templateUrl: './balance-sheet.component.html',
  styleUrls: ['./balance-sheet.component.scss']
})
export class BalanceSheetComponent implements OnInit {
  public isLoading: boolean = true;
  public searchKeyword: string = "";
  public fromDate: string;
  public toDate: string;
  public type: string = "asset";
  public balanceSheetReports: BalanceSheetReport = { assets: {}, liabilities: {}, equity: {} };


  constructor(
    private commonService: CommonService,
    private datePipe: DatePipe,
    private financeService: FinanceService
  ) { }

  ngOnInit(): void {
    this.fromDate = this.datePipe.transform(this.commonService.adjustDateByDays(-30), 'yyyy-MM-dd');
    this.toDate = this.datePipe.transform(this.commonService.adjustDateByDays(30), 'yyyy-MM-dd');
    this.getBalanceSheetReport();
  }

  public getBalanceSheetReport(clear: boolean = true) {
    if (clear) {
      this.isLoading = true;
    }
    if (!this.fromDate || !this.toDate) {
      this.commonService.setToastr(500, "Both \"From Date\" and \"To Date\" are required to proceed.");
      return;
    }
    const fromDateFormatted = this.datePipe.transform(this.fromDate, 'dd-MM-yyyy');
    const toDateFormatted = this.datePipe.transform(this.toDate, 'dd-MM-yyyy');
    this.financeService.getBalanceSheetReport(fromDateFormatted, toDateFormatted).subscribe({
      next: (balanceSheetReport: BalanceSheetReport) => {
        this.balanceSheetReports = balanceSheetReport;
        this.balanceSheetReports.liabilities = {
          ...this.balanceSheetReports.liabilities,
          ...this.balanceSheetReports.equity
        };
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.commonService.setToastr(500, error?.error?.name || error.error);
      }
    });
  }

  get liabilitiesTotal(): number {
    return Object.values(this.balanceSheetReports?.liabilities || {}).reduce((a, b) => a + b, 0);
  }

  get assetsTotal(): number {
    return Object.values(this.balanceSheetReports?.assets || {}).reduce((a, b) => a + b, 0);
  }

  get maxRows(): number {
    const liabilitiesCount = Object.keys(this.balanceSheetReports?.liabilities || {}).length;
    const assetsCount = Object.keys(this.balanceSheetReports?.assets || {}).length;
    return Math.max(liabilitiesCount, assetsCount);
  }

  public downloadExcel() {
    const liabilities = Object.entries(this.balanceSheetReports.liabilities || {});
    const assets = Object.entries(this.balanceSheetReports.assets || {});
    const maxLength = Math.max(liabilities.length, assets.length);

    const tableData: any[] = [];

    for (let i = 0; i < maxLength; i++) {
      const liability = liabilities[i];
      const asset = assets[i];

      tableData.push({
        '#': i + 1,
        'Liabilities': liability ? liability[0] : '',
        'Amount (Liabilities)': liability ? liability[1] : '',
        'Assets': asset ? asset[0] : '',
        'Amount (Assets)': asset ? asset[1] : ''
      });
    }

    // Add totals row
    tableData.push({
      '#': '',
      'Liabilities': 'Total Liabilities',
      'Amount (Liabilities)': this.liabilitiesTotal,
      'Assets': 'Total Assets',
      'Amount (Assets)': this.assetsTotal
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(tableData);
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Balance Sheet Report': worksheet },
      SheetNames: ['Balance Sheet Report']
    };

    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fileName = `Balance_Sheet_Report_${this.datePipe.transform(new Date(), 'yyyy-MM-dd')}.xlsx`;
    saveAs(data, fileName);
  }
}


